import { useEffect, useMemo, useState } from 'react';
import { Mail, Eye, RefreshCw, CheckCircle2, XCircle, Send } from 'lucide-react';
import Pill from '../components/ui/Pill.jsx';
import Modal from '../components/ui/Modal.jsx';
import PdfPreviewModal from '../components/PdfPreviewModal.jsx';
import { api } from '../api/client.js';
import { formatDateFr } from '../lib/dates.js';

const DEFAULT_BODY =
  "Bonjour,\n\nVeuillez trouver ci-joint votre Compte-Rendu d'Activité pour la période concernée.\n\nCordialement,\nL'équipe Synotis";

export default function MailView({ params = {} }) {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCraIds, setSelectedCraIds] = useState(new Set());
  const [filterCollab, setFilterCollab] = useState('');
  const [subject, setSubject] = useState('Votre CRA Synotis - {mois}');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [previewCraId, setPreviewCraId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Charge les jobs terminés + pré-sélectionne celui passé en params
  useEffect(() => {
    api.cra
      .listJobs({ limit: 30 })
      .then(({ jobs }) => {
        const completed = jobs.filter((j) => j.status === 'COMPLETED' || j.status === 'PARTIAL');
        setJobs(completed);
        if (params.jobId) {
          const target = completed.find((j) => j.id === params.jobId);
          if (target) setSelectedJob(target);
          else api.cra.getJob(params.jobId).then((j) => {
            setJobs((prev) => [j, ...prev]);
            setSelectedJob(j);
          }).catch(() => {});
        }
      })
      .catch(() => setJobs([]));
  }, [params.jobId]);

  // Charge les CraFile du job sélectionné
  useEffect(() => {
    if (!selectedJob) return;
    api.cra
      .getJob(selectedJob.id)
      .then((job) => setSelectedJob(job))
      .catch(() => {});
  }, [selectedJob?.id]);

  const craFiles = selectedJob?.craFiles ?? [];

  const filteredCra = useMemo(() => {
    const lower = filterCollab.trim().toLowerCase();
    if (!lower) return craFiles;
    return craFiles.filter(
      (c) =>
        c.consultantName.toLowerCase().includes(lower) ||
        c.consultantEmail.toLowerCase().includes(lower) ||
        c.clientName.toLowerCase().includes(lower),
    );
  }, [craFiles, filterCollab]);

  const toggleCra = (id) => {
    setSelectedCraIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    const allSelected = filteredCra.every((c) => selectedCraIds.has(c.id));
    setSelectedCraIds((prev) => {
      const next = new Set(prev);
      filteredCra.forEach((c) => {
        if (allSelected) next.delete(c.id);
        else next.add(c.id);
      });
      return next;
    });
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setSendResult(null);
    try {
      const result = await api.mail.sendCraBatch(Array.from(selectedCraIds), {
        subject,
        body,
      });
      setSendResult({ ok: true, ...result });
      setSelectedCraIds(new Set());
    } catch (err) {
      setSendResult({ ok: false, error: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Choix de la génération</h2>
        {jobs.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>Aucune génération disponible.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => {
                  setSelectedJob(j);
                  setSelectedCraIds(new Set());
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  background: selectedJob?.id === j.id ? 'var(--primary)' : 'var(--glass)',
                  color: selectedJob?.id === j.id ? '#fff' : 'var(--text)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {j.startDate?.slice(0, 10)} → {j.endDate?.slice(0, 10)}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>
                    {j.totalPdfs} CRA · {formatDateFr(j.startedAt)}
                  </div>
                </div>
                {j.dryRun && (
                  <span
                    style={{
                      padding: '0.2rem 0.5rem',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: '0.7rem',
                      background: 'var(--warning)',
                      color: '#fff',
                    }}
                  >
                    dry-run
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {selectedJob && (
        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>
            CRA à envoyer ({filteredCra.length} affichés, {selectedCraIds.size} sélectionnés)
          </h2>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder="Filtrer par collaborateur ou client…"
              value={filterCollab}
              onChange={(e) => setFilterCollab(e.target.value)}
              style={{ ...inputStyle, flex: '1 1 240px' }}
            />
            <Pill onClick={toggleAllFiltered} disabled={filteredCra.length === 0}>
              {filteredCra.every((c) => selectedCraIds.has(c.id))
                ? 'Désélectionner tout'
                : 'Tout sélectionner (filtré)'}
            </Pill>
          </div>

          <div
            style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {filteredCra.map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.6rem 0.9rem',
                  borderBottom: '1px solid var(--glass-border)',
                  background: selectedCraIds.has(c.id) ? 'var(--glass)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCraIds.has(c.id)}
                  onChange={() => toggleCra(c.id)}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{c.consultantName}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {c.clientName} · {c.projectName} · {c.totalDays} j
                  </div>
                </div>
                {c.emailSentAt && (
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--success)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                    title={`Envoyé le ${formatDateFr(c.emailSentAt)}`}
                  >
                    <CheckCircle2 size={14} />
                    Envoyé
                  </span>
                )}
                <button
                  onClick={() => setPreviewCraId(c.id)}
                  title="Prévisualiser le PDF"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.3rem 0.6rem',
                    background: 'transparent',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                  }}
                >
                  <Eye size={14} /> Aperçu
                </button>
              </div>
            ))}
            {filteredCra.length === 0 && (
              <p style={{ color: 'var(--text-muted)', padding: '1rem', textAlign: 'center' }}>
                Aucun CRA ne correspond au filtre.
              </p>
            )}
          </div>
        </section>
      )}

      {selectedJob && selectedCraIds.size > 0 && (
        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Contenu du message</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0 }}>
            Variables disponibles : <code>{'{collaborateur}'}</code>, <code>{'{client}'}</code>,{' '}
            <code>{'{mois}'}</code>, <code>{'{periode}'}</code>
          </p>
          <label style={labelStyle}>
            Sujet
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={{ ...labelStyle, marginTop: '0.75rem' }}>
            Corps du message
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </label>

          <div style={{ marginTop: '1rem' }}>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={sending}
              style={primaryButtonStyle(sending)}
            >
              {sending ? <RefreshCw size={16} className="spin" /> : <Send size={16} />}
              {sending ? 'Envoi…' : `Envoyer ${selectedCraIds.size} CRA`}
            </button>
          </div>

          {sendResult && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                borderRadius: 'var(--radius-md)',
                background: sendResult.ok ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: sendResult.ok ? 'var(--success)' : 'var(--danger)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              {sendResult.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
              {sendResult.ok
                ? `${sendResult.sent ?? '?'} email(s) envoyé(s)`
                : `Erreur : ${sendResult.error}`}
            </div>
          )}
        </section>
      )}

      <PdfPreviewModal
        craFileId={previewCraId}
        onClose={() => setPreviewCraId(null)}
      />

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmer l'envoi"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} style={secondaryButtonStyle}>
              Annuler
            </button>
            <button onClick={handleSend} style={primaryButtonStyle(false)}>
              <Mail size={16} /> Envoyer
            </button>
          </>
        }
      >
        <p>
          Vous êtes sur le point d'envoyer <strong>{selectedCraIds.size} CRA</strong> aux
          collaborateurs concernés.
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          Chaque destinataire reçoit son propre CRA en pièce jointe via{' '}
          <code>no-reply@synotis.ch</code>.
        </p>
      </Modal>
    </div>
  );
}

const cardStyle = {
  background: 'var(--card-bg)',
  padding: '1.5rem',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleStyle = { margin: '0 0 1rem 0', fontSize: '1rem', color: 'var(--primary)' };
const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};
const primaryButtonStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 1.25rem',
  background: disabled ? 'var(--text-muted)' : 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
});
const secondaryButtonStyle = {
  padding: '0.6rem 1.25rem',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: '0.9rem',
};
